import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import Loader from "@/components/Loader";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AmbientDock from "@/components/AmbientDock";
import Particles from "@/components/Particles";
import Search from "@/components/Search";
import MusicDock from "@/components/MusicDock";
import { MusicPlayerProvider } from "@/context/MusicPlayer";
import { AmbienceProvider } from "@/context/Ambience";
import { trackPageview } from "@/lib/analytics";

import Home from "@/pages/Home";
import Releases from "@/pages/Releases";
import ReleaseDetail from "@/pages/ReleaseDetail";
import Lyrics from "@/pages/Lyrics";
import LyricDetail from "@/pages/LyricDetail";
import About from "@/pages/About";
import Press from "@/pages/Press";
import Licensing from "@/pages/Licensing";
import Contact from "@/pages/Contact";
import Support from "@/pages/Support";
import Festival from "@/pages/Festival";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageview();
  }, [pathname]);
  return null;
};

const PageShell = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/releases" element={<Releases />} />
          <Route path="/releases/:id" element={<ReleaseDetail />} />
          <Route path="/lyrics" element={<Lyrics />} />
          <Route path="/lyrics/:id" element={<LyricDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/press" element={<Press />} />
          <Route path="/licensing" element={<Licensing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Support />} />
          <Route path="/festivals/:slug" element={<Festival />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
};

const Shell = () => {
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <BrowserRouter>
      <ScrollTop />
      <Particles />
      <Nav onOpenSearch={() => setSearchOpen(true)} />
      <Search open={searchOpen} onClose={() => setSearchOpen(false)} />
      <PageShell />
      <Footer />
      <MusicDock />
      <AmbientDock />
    </BrowserRouter>
  );
};

function App() {
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem("kx_booted");
    if (seen) setBooted(true);
    else {
      const t = setTimeout(() => {
        sessionStorage.setItem("kx_booted", "1");
        setBooted(true);
      }, 3600);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AnimatePresence>{!booted && <Loader key="loader" />}</AnimatePresence>
      {booted && (
        <MusicPlayerProvider>
          <AmbienceProvider>
            <Shell />
          </AmbienceProvider>
        </MusicPlayerProvider>
      )}
      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(255,153,51,0.35)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
}

export default App;
